import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { SpendReport, insightsApi } from '../../services/insightsApi';
import { SectionTitle, StatCard, fmtNum, fmtPct } from '../performance/common';
import { TargetLink, fmtChange, fmtCredits, useReport } from './common';

const SOURCE_COLORS = ['#1976d2', '#43a047', '#fb8c00', '#8e24aa', '#00897b', '#e53935'];
const OTHER_COLOR = '#bdbdbd';
const TABLE_ROWS = 25;

const round2 = (n: number) => Math.round(n * 100) / 100;

const monthName = (label: string) => {
  const [y, m] = label.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

const ChangeText: React.FC<{ current: number; previous: number }> = ({ current, previous }) => {
  if (!previous) return <Typography variant="body2" color="text.secondary">{current ? 'new' : '–'}</Typography>;
  const change = (current - previous) / previous;
  return (
    <Typography variant="body2" color={change > 0.1 ? 'warning.main' : change < -0.1 ? 'success.main' : 'text.secondary'}>
      {fmtChange(change)}
    </Typography>
  );
};

const ShareBar: React.FC<{ share: number | null }> = ({ share }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
    <LinearProgress variant="determinate" value={Math.min(100, (share ?? 0) * 100)} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
    <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right' }}>
      {fmtPct(share, 0)}
    </Typography>
  </Stack>
);

export const SpendView: React.FC<{ customerId?: string | null }> = ({ customerId }) => {
  const { getIdToken } = useAuth();
  const api = useMemo(() => insightsApi(getIdToken), [getIdToken]);
  const load = useCallback(() => api.spend(customerId), [api, customerId]);
  const { data, loading, error } = useReport<SpendReport>(load);
  const [by, setBy] = useState<'source' | 'model'>('source');

  const cumulative = useMemo(() => {
    if (!data) return [];
    let cur = 0;
    let prev = 0;
    const today = data.daily.filter(d => d.current !== null).length;
    const { daily: rate } = data.projection.run_rate;
    const elapsed = data.projection.elapsed_days;
    return data.daily.map(d => {
      if (d.current !== null) cur += d.current;
      if (d.previous !== null) prev += d.previous;
      // Points are end-of-day totals; the last one equals the run-rate projection.
      const projected = d.day >= today ? round2(data.totals.mtd + rate * Math.max(0, d.day - elapsed)) : null;
      return {
        label: String(d.day),
        daily: d.current,
        current: d.current === null ? null : round2(cur),
        previous: d.previous === null ? null : round2(prev),
        projected,
      };
    });
  }, [data]);

  const stacked = useMemo(() => {
    if (!data) return [];
    return data.daily
      .filter(d => d.current !== null)
      .map(d => ({ label: String(d.day), ...(d.sources ?? {}), other: d.other ?? 0 }));
  }, [data]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) {
    return loading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    ) : null;
  }

  const { totals, projection } = data;
  const thisMonth = monthName(data.month.label);
  const lastMonth = monthName(data.previous_month.label);
  const nothing = totals.mtd === 0 && totals.previous_month === 0;
  const allowanceShare = data.allowance ? totals.mtd / data.allowance : null;
  const projectedOver = data.allowance ? projection.run_rate.projected > data.allowance : false;

  return (
    <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 120ms' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 820 }}>
        AI credits used by every successful run on your account, calculated the same way as your Subscription page. Runs made as chain
        steps (including batch jobs) are counted under their chain, not under the function, so nothing is counted twice. Failed runs are
        not charged. Days are UTC calendar days.
      </Typography>

      {nothing ? (
        <Alert severity="info">No AI credits used in {lastMonth} or {thisMonth} so far.</Alert>
      ) : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <StatCard
                label={`${thisMonth} so far`}
                value={fmtCredits(totals.mtd)}
                caption={`${fmtNum(totals.runs_mtd)} charged runs · ${projection.elapsed_days.toFixed(1)} of ${projection.days_in_month} days`}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Projected month (recent pace)"
                value={fmtCredits(projection.run_rate.projected)}
                caption={`${fmtCredits(projection.run_rate.daily)}/day, the average of the last 7 full days, for ${projection.remaining_days.toFixed(1)} remaining days`}
                tooltip="Credits so far plus the last 7 full days' daily average for each remaining day. Reacts quickly when usage changes."
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Projected month (month-to-date pace)"
                value={projection.naive.projected === null ? '–' : fmtCredits(projection.naive.projected)}
                caption={
                  projection.naive.daily === null
                    ? 'Available after the first full day'
                    : `${fmtCredits(projection.naive.daily)}/day, credits so far ÷ days elapsed × ${projection.days_in_month}`
                }
                tooltip="A straight line: this month's average day so far, times the days in the month."
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label={lastMonth}
                value={fmtCredits(totals.previous_month)}
                caption={
                  <>
                    Same point last month: {fmtCredits(totals.previous_same_point)}
                    {totals.change_vs_same_point !== null && ` (${fmtChange(totals.change_vs_same_point)} now)`}
                  </>
                }
                tooltip="Credits used in the previous calendar month, and by the same day and hour of that month."
              />
            </Grid>
          </Grid>

          {(data.allowance || data.billing_period) && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              {data.allowance && (
                <Box sx={{ mb: data.billing_period ? 1.5 : 0 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2">
                      {fmtCredits(totals.mtd)} of {fmtNum(data.allowance)} plan credits this calendar month ({fmtPct(allowanceShare, 0)})
                    </Typography>
                    {projectedOver && <Chip size="small" color="warning" label="Projected to go over" />}
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    color={projectedOver ? 'warning' : 'primary'}
                    value={Math.min(100, (allowanceShare ?? 0) * 100)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              )}
              {data.billing_period && (
                <Typography variant="body2" color="text.secondary">
                  Your billing period started {new Date(data.billing_period.since).toLocaleDateString()}: {fmtCredits(data.billing_period.credits)}{' '}
                  credits used since then. That is the number your Subscription page shows; the figures here use calendar months.
                </Typography>
              )}
            </Paper>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <SectionTitle
                  title="Credits by day of month"
                  help={`Bars: ${thisMonth} per day. Lines: running totals for ${thisMonth} and ${lastMonth}; dashed: projection at the recent pace.`}
                />
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={cumulative} margin={{ top: 10, right: 8, left: -4, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={8} />
                      <YAxis yAxisId="d" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="c" orientation="right" tick={{ fontSize: 11 }} />
                      <ChartTooltip formatter={(v: number) => fmtCredits(v)} labelFormatter={l => `Day ${l}`} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar yAxisId="d" dataKey="daily" name="Per day" fill="#90caf9" />
                      <Line yAxisId="c" dataKey="current" name={`${thisMonth} total`} stroke="#1976d2" strokeWidth={2} dot={false} connectNulls />
                      <Line yAxisId="c" dataKey="previous" name={`${lastMonth} total`} stroke="#9e9e9e" dot={false} connectNulls />
                      <Line yAxisId="c" dataKey="projected" name="Projected" stroke="#1976d2" strokeDasharray="5 4" dot={false} connectNulls />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <SectionTitle title="Where it went" help={`${thisMonth} per day, by your largest functions and chains.`} />
                {stacked.length === 0 || data.top_sources.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No credits used this month yet.
                  </Typography>
                ) : (
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer>
                      <BarChart data={stacked} margin={{ top: 10, right: 8, left: -8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={8} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <ChartTooltip formatter={(v: number) => fmtCredits(v)} labelFormatter={l => `Day ${l}`} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {data.top_sources.map((s, i) => (
                          <Bar key={s.key} dataKey={s.key} stackId="s" name={s.name} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                        ))}
                        <Bar dataKey="other" stackId="s" name="Everything else" fill={OTHER_COLOR} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <SectionTitle
              title="Breakdown"
              help={`${thisMonth} so far, with ${lastMonth} for comparison. Change compares the full previous month, so early in the month most rows read lower.`}
              action={
                <ToggleButtonGroup size="small" exclusive value={by} onChange={(_, v) => v && setBy(v)}>
                  <ToggleButton value="source">Functions & chains</ToggleButton>
                  <ToggleButton value="model">Models</ToggleButton>
                </ToggleButtonGroup>
              }
            />
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{by === 'source' ? 'Function or chain' : 'Model'}</TableCell>
                    <TableCell align="right">Credits</TableCell>
                    <TableCell>Share</TableCell>
                    <TableCell align="right">Charged runs</TableCell>
                    <TableCell align="right">Credits / 1k runs</TableCell>
                    <TableCell align="right">{lastMonth}</TableCell>
                    <TableCell align="right">Change</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(by === 'source' ? data.sources : data.models).slice(0, TABLE_ROWS).map(row => {
                    const key = 'model' in row ? row.model : `${row.type}:${row.name}:${row.id}`;
                    return (
                      <TableRow key={key}>
                        <TableCell>
                          {'model' in row ? (
                            <Chip size="small" variant="outlined" label={row.model} />
                          ) : (
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Chip size="small" variant="outlined" label={row.type} sx={{ height: 18, fontSize: 10.5 }} />
                              <Typography variant="body2">
                                <TargetLink type={row.type} id={row.id} name={row.name} customerId={customerId} />
                              </Typography>
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell align="right">{fmtCredits(row.credits)}</TableCell>
                        <TableCell>
                          <ShareBar share={row.share} />
                        </TableCell>
                        <TableCell align="right">{fmtNum(row.runs)}</TableCell>
                        <TableCell align="right">{row.runs ? fmtCredits((row.credits / row.runs) * 1000) : '–'}</TableCell>
                        <TableCell align="right">{fmtCredits(row.previous_credits)}</TableCell>
                        <TableCell align="right">
                          <ChangeText current={row.credits} previous={row.previous_credits} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            {(by === 'source' ? data.sources : data.models).length > TABLE_ROWS && (
              <Typography variant="caption" color="text.secondary">
                Showing the top {TABLE_ROWS}.
              </Typography>
            )}
            {by === 'source' && (
              <Tooltip title="Chain step runs are matched to their chain by the chain id saved on the run, or through the chain execution.">
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, cursor: 'help' }}>
                  Chain rows add up the credits of all steps in that chain's runs.
                </Typography>
              </Tooltip>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default SpendView;
